import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, UserPlus, Shield, Check, X, Search, Trash2, FileText, Download, Filter, RefreshCw, Plus } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { usePermissions } from '@/hooks/use-permissions';
import { SearchableSelect } from '@/components/ui/searchable-select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserAccessControl({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions(user);
  
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Create user states
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [newUserData, setNewUserData] = useState({
    username: '',
    password: '',
    name: '',
    title: '',
    department: '',
    access_level: '',
    gps_restricted: false
  });
  
  // Delete user states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteAuthDialogOpen, setDeleteAuthDialogOpen] = useState(false);

  // Employee Actions Ledger states
  const [logs, setLogs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  
  const [filters, setFilters] = useState({
    user_id: '',
    action_type: '',
    module_name: '',
    start_date: '',
    end_date: '',
    limit: 50,
    skip: 0
  });
  const [viewLogDetails, setViewLogDetails] = useState(null);
  const [deleteLogDialogOpen, setDeleteLogDialogOpen] = useState(false);
  const [logToDelete, setLogToDelete] = useState(null);
  const [deleteLogAuthDialogOpen, setDeleteLogAuthDialogOpen] = useState(false);
  
  // Predefined options
  const actionTypes = [
    'CREATE', 'UPDATE', 'DELETE', 'APPROVE', 'REJECT', 
    'INWARD', 'OUTWARD', 'UTILIZE', 'START', 'PAUSE', 
    'STOP', 'RESUME', 'COMPLETE'
  ];
  
  const moduleNames = [
    'Master Data - Fabric', 'Master Data - Trims', 'Master Data - Packaging',
    'Master Data - Article', 'Master Data - Digital Design', 'Fashion Design',
    'Production Orders', 'Purchase Orders', 'User Management', 'Wastage',
    'Gate Pass', 'Expenses', 'Inventory - Raw Goods', 'Work Orders',
    'External Work Orders', 'Showroom', 'Tech Pack'
  ];

  useEffect(() => {
    fetchUsers();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [filters.skip, filters.limit]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setAllUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    }
  };

  const fetchLogs = async () => {
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value.toString());
      });
      
      const response = await axios.get(`${API}/audit-logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch audit logs');
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, skip: 0 }));
  };

  const handleClearFilters = () => {
    setFilters({
      user_id: '',
      action_type: '',
      module_name: '',
      start_date: '',
      end_date: '',
      limit: 50,
      skip: 0
    });
  };

  const handleDeleteLogClick = (log) => {
    setLogToDelete(log);
    setDeleteLogDialogOpen(true);
  };

  const handleDeleteLog = () => {
    setDeleteLogDialogOpen(false);
    setDeleteLogAuthDialogOpen(true);
  };

  const confirmDeleteLog = async () => {
    if (!logToDelete) return;
    
    setLoadingLogs(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/audit-logs/${logToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Audit log deleted successfully');
        setDeleteLogAuthDialogOpen(false);
        setLogToDelete(null);
        fetchLogs();
      } else {
        toast.error('Failed to delete audit log');
      }
    } catch (error) {
      console.error('Error deleting audit log:', error);
      toast.error('Failed to delete audit log');
    } finally {
      setLoadingLogs(false);
    }
  };

  // GPS Location Management Functions removed

  const handleCreateUser = async () => {
    if (!newUserData.username || !newUserData.password || !newUserData.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          ...newUserData,
          status: 'Active',
          design_studio_access: false,
          task_management_access: false,
          user_access_control: false
        })
      });
      
      if (response.ok) {
        toast.success('User created successfully');
        setCreateUserDialogOpen(false);
        setNewUserData({
          username: '',
          password: '',
          name: '',
          title: '',
          department: '',
          access_level: ''
        });
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteUser = () => {
    setDeleteDialogOpen(false);
    setDeleteAuthDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('User deleted successfully');
        setDeleteAuthDialogOpen(false);
        setUserToDelete(null);
        fetchUsers();
      } else {
        toast.error('Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleManagePermissions = async (user) => {
    setSelectedUser(user);
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/users/${user.id}/permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const perms = await response.json();
        setPermissions(perms);
        setPermissionsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      toast.error('Failed to fetch permissions');
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionToggle = (permissionKey) => {
    setPermissions(prev => ({
      ...prev,
      [permissionKey]: !prev[permissionKey]
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUser || !permissions) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      // Update module access flags (basic user info only - NO gps_restricted here)
      const moduleAccessUpdate = {
        design_studio_access: permissions.design_studio_access || false,
        task_management_access: permissions.task_management_access || false,
        user_access_control: permissions.user_access_control || false
      };
      
      // Update user's module access (send complete user data to avoid overwriting)
      const userUpdateData = {
        username: selectedUser.username,
        name: selectedUser.name,
        title: selectedUser.title || '',
        department: selectedUser.department || '',
        access_level: selectedUser.access_level || '',
        password: selectedUser.password,
        ...moduleAccessUpdate
      };
      
      await fetch(`${API}/users/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(userUpdateData)
      });
      
      // Update detailed permissions
      const response = await fetch(`${API}/users/${selectedUser.id}/permissions`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ permissions })
      });
      
      if (response.ok) {
        // If updating current user's permissions, refresh localStorage
        if (currentUser.id === selectedUser.id) {
          const updatedUser = {
            ...currentUser,
            ...moduleAccessUpdate,
            ...permissions
          };
          localStorage.setItem('user', JSON.stringify(updatedUser));
          toast.success('Permissions updated successfully. Changes applied to your session.');
          
          // Reload page to apply changes
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        } else {
          toast.success('Permissions updated successfully');
        }
        
        setPermissionsDialogOpen(false);
        fetchUsers();
      } else {
        toast.error('Failed to update permissions');
      }
    } catch (error) {
      console.error('Error updating permissions:', error);
      toast.error('Failed to update permissions');
    } finally {
      setLoading(false);
    }
  };

  const handleGrantAll = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${API}/users/${userId}/permissions/grant-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('All permissions granted');
        fetchUsers();
        
        if (selectedUser && selectedUser.id === userId) {
          const permsResponse = await fetch(`${API}/users/${userId}/permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (permsResponse.ok) {
            const perms = await permsResponse.json();
            setPermissions(perms);
          }
        }
        
        // If updating current user's permissions, refresh localStorage
        if (currentUser.id === userId) {
          const updatedUserResponse = await fetch(`${API}/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (updatedUserResponse.ok) {
            const updatedUserData = await updatedUserResponse.json();
            localStorage.setItem('user', JSON.stringify(updatedUserData));
            toast.success('Your session has been updated with all permissions');
            
            // Reload page to apply changes
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
      }
    } catch (error) {
      console.error('Error granting permissions:', error);
      toast.error('Failed to grant permissions');
    }
  };

  const handleRevokeAll = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${API}/users/${userId}/permissions/revoke-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('All permissions revoked');
        fetchUsers();
        
        if (selectedUser && selectedUser.id === userId) {
          const permsResponse = await fetch(`${API}/users/${userId}/permissions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (permsResponse.ok) {
            const perms = await permsResponse.json();
            setPermissions(perms);
          }
        }
        
        // If updating current user's permissions, refresh localStorage
        if (currentUser.id === userId) {
          const updatedUserResponse = await fetch(`${API}/users/${userId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (updatedUserResponse.ok) {
            const updatedUserData = await updatedUserResponse.json();
            localStorage.setItem('user', JSON.stringify(updatedUserData));
            toast.success('Your session has been updated');
            
            // Reload page to apply changes
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          }
        }
      }
    } catch (error) {
      console.error('Error revoking permissions:', error);
      toast.error('Failed to revoke permissions');
    }
  };

  const filteredUsers = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.department?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const permissionGroups = {
    'Task Management': [
      { key: 'task_design_sampling', label: 'Design & Sampling' },
      { key: 'task_merchandise', label: 'Merchandise' },
      { key: 'task_digital_design', label: 'Digital Design' },
      { key: 'task_meeting_minutes', label: 'Meeting Minutes' },
      { key: 'task_weekly_progress', label: 'Weekly Progress' }
    ],
    'Master Data': [
      { key: 'master_data_view', label: 'View Master Data' },
      { key: 'master_data_create', label: 'Create Master Data' },
      { key: 'master_data_edit', label: 'Edit Master Data' },
      { key: 'master_data_delete', label: 'Delete Master Data' }
    ],
    'Design HQ': [
      { key: 'fashion_design_view', label: 'View Designs' },
      { key: 'fashion_design_create', label: 'Create Designs' },
      { key: 'fashion_design_edit', label: 'Edit Designs' },
      { key: 'fashion_design_delete', label: 'Delete Designs' },
      { key: 'fashion_design_approve', label: 'Approve Designs' }
    ],
    'Sampling Time Tracker': [
      { key: 'sampling_time_tracker_view', label: 'View Sampling' },
      { key: 'sampling_time_tracker_create', label: 'Create Sampling' },
      { key: 'sampling_time_tracker_edit', label: 'Edit Sampling' },
      { key: 'sampling_time_tracker_delete', label: 'Delete Sampling' }
    ],
    'Showroom': [
      { key: 'showroom_view', label: 'View Showroom' },
      { key: 'showroom_edit', label: 'Edit Showroom' }
    ],
    'Tech Pack': [
      { key: 'view_tech_pack', label: 'View Tech Pack' },
      { key: 'upload_tech_pack', label: 'Upload Tech Pack' }
    ],
    'LN-X Reports': [
      { key: 'lnx_reports_view', label: 'View LN-X Reports' }
    ],
    'Pre-Production Costing': [
      { key: 'view_costing', label: 'View Costing' },
      { key: 'view_pricing', label: 'View Pricing' },
      { key: 'edit_costing', label: 'Edit Costing' },
      { key: 'approve_costing', label: 'Approve Costing' }
    ],
    'Internal Work Orders': [
      { key: 'work_orders_view', label: 'View Work Orders' },
      { key: 'work_orders_create', label: 'Create Work Orders' },
      { key: 'work_orders_edit', label: 'Edit Work Orders' },
      { key: 'work_orders_delete', label: 'Delete Work Orders' }
    ],
    'External Work Orders': [
      { key: 'external_work_orders_view', label: 'View EWOs' },
      { key: 'external_work_orders_create', label: 'Create EWOs' },
      { key: 'external_work_orders_edit', label: 'Edit EWOs' },
      { key: 'external_work_orders_delete', label: 'Delete EWOs' },
      { key: 'external_work_orders_approve', label: 'Approve EWOs' },
      { key: 'external_work_orders_execute', label: 'Execute EWOs' }
    ],
    'Purchase Orders': [
      { key: 'purchase_orders_view', label: 'View POs' },
      { key: 'purchase_orders_create', label: 'Create POs' },
      { key: 'purchase_orders_edit', label: 'Edit POs' },
      { key: 'purchase_orders_delete', label: 'Delete POs' },
      { key: 'purchase_orders_approve', label: 'Approve POs' }
    ],
    'Expenses': [
      { key: 'expenses_view', label: 'View Expenses' },
      { key: 'expenses_create', label: 'Create Expenses' },
      { key: 'expenses_edit', label: 'Edit Expenses' },
      { key: 'expenses_delete', label: 'Delete Expenses' },
      { key: 'expenses_approve', label: 'Approve Expenses' },
      { key: 'expenses_execute', label: 'Execute Expenses' }
    ],
    'Wastage': [
      { key: 'wastage_view', label: 'View Wastage' },
      { key: 'wastage_create', label: 'Create Wastage' },
      { key: 'wastage_edit', label: 'Edit Wastage' },
      { key: 'wastage_delete', label: 'Delete Wastage' },
      { key: 'wastage_utilize', label: 'Utilize Wastage' }
    ],
    'Gate Pass': [
      { key: 'gate_pass_view', label: 'View Gate Pass' },
      { key: 'gate_pass_create', label: 'Create Gate Pass' },
      { key: 'gate_pass_edit', label: 'Edit Gate Pass' },
      { key: 'gate_pass_delete', label: 'Delete Gate Pass' },
      { key: 'gate_pass_approve', label: 'Approve Gate Pass' }
    ],
    'Raw Goods Inventory': [
      { key: 'inventory_view', label: 'View Inventory' },
      { key: 'inventory_receive', label: 'Receive Inventory' },
      { key: 'inventory_issue', label: 'Issue Inventory' }
    ]
  };

  // Check permission to access User Access Control
  if (!hasPermission('user_access_control')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">You don't have permission to access User Access Control.</p>
          <Button onClick={() => navigate('/dashboard')}>Go to Dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-black">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                User Access Control
              </h1>
              <p className="text-sm text-gray-600 mt-1">Manage user permissions and access rights</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-6 py-8">
        {/* Search and Add User */}
        <div className="mb-6 flex justify-between items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              type="text"
              placeholder="Search users by name, username, or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-2 border-black"
            />
          </div>
          <Button
            onClick={() => setCreateUserDialogOpen(true)}
            className="bg-black hover:bg-gray-800"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            New User
          </Button>
        </div>

        {/* Users Table */}
        <div className="bg-white border-2 border-black overflow-hidden">
          <table className="w-full">
            <thead className="bg-black text-white">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Department</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Access Level</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold">{u.name}</p>
                      <p className="text-sm text-gray-600">{u.username}</p>
                      <p className="text-xs text-gray-500">{u.title}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm">{u.department}</td>
                  <td className="px-6 py-4 text-sm">{u.access_level}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 text-xs font-semibold rounded ${
                      u.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => handleManagePermissions(u)}
                      className="bg-black hover:bg-gray-800"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Manage Permissions
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDeleteClick(u)}
                      className="border-red-500 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
              {filteredUsers.length === 0 && (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                    No users found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Employee Actions Ledger Section */}
        <div className="mt-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Employee Actions Ledger
              </h2>
              <p className="text-sm text-gray-600 mt-1">Track and monitor all employee actions across the system</p>
            </div>
            <Button
              onClick={() => fetchLogs()}
              variant="outline"
              className="gap-2"
              disabled={loadingLogs}
            >
              <RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Filters */}
          <div className="bg-white border-2 border-black p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label className="font-bold mb-2 block">Employee</Label>
                <SearchableSelect
                  value={filters.user_id}
                  onChange={(value) => handleFilterChange('user_id', value)}
                  options={[
                    { value: '', label: 'All Employees' },
                    ...allUsers.map(u => ({
                      value: u.id,
                      label: `${u.name} (${u.username})`
                    }))
                  ]}
                  placeholder="Select employee..."
                />
              </div>
              
              <div>
                <Label className="font-bold mb-2 block">Action Type</Label>
                <SearchableSelect
                  value={filters.action_type}
                  onChange={(value) => handleFilterChange('action_type', value)}
                  options={[
                    { value: '', label: 'All Actions' },
                    ...actionTypes.map(action => ({
                      value: action,
                      label: action
                    }))
                  ]}
                  placeholder="Select action..."
                />
              </div>
              
              <div>
                <Label className="font-bold mb-2 block">Module</Label>
                <SearchableSelect
                  value={filters.module_name}
                  onChange={(value) => handleFilterChange('module_name', value)}
                  options={[
                    { value: '', label: 'All Modules' },
                    ...moduleNames.map(module => ({
                      value: module,
                      label: module
                    }))
                  ]}
                  placeholder="Select module..."
                />
              </div>
              
              <div>
                <Label className="font-bold mb-2 block">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  className="border-2 border-black"
                />
              </div>
              
              <div>
                <Label className="font-bold mb-2 block">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  className="border-2 border-black"
                />
              </div>
              
              <div className="flex items-end">
                <Button
                  onClick={handleClearFilters}
                  variant="outline"
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
            
            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => fetchLogs()}
                className="bg-black hover:bg-gray-800"
                disabled={loadingLogs}
              >
                <Search className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </div>

          {/* Audit Logs Table */}
          <div className="bg-white border-2 border-black overflow-hidden">
            <table className="w-full">
              <thead className="bg-black text-white">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Timestamp</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Employee</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Action</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Module</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Entity</th>
                  <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loadingLogs ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      Loading logs...
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        {new Date(log.timestamp).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold">{log.user_name}</p>
                          <p className="text-xs text-gray-500">{log.user_email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 text-xs font-semibold rounded ${
                          log.action_type === 'CREATE' ? 'bg-green-100 text-green-800' :
                          log.action_type === 'UPDATE' ? 'bg-blue-100 text-blue-800' :
                          log.action_type === 'DELETE' ? 'bg-red-100 text-red-800' :
                          log.action_type === 'APPROVE' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {log.action_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">{log.module_name}</td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-semibold">{log.entity_type}</p>
                          {log.entity_code && (
                            <p className="text-xs text-gray-500">{log.entity_code}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setViewLogDetails(log)}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteLogClick(log)}
                            className="border-red-500 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>

      {/* Log Details Dialog */}
      <Dialog open={!!viewLogDetails} onOpenChange={() => setViewLogDetails(null)}>
        <DialogContent className="max-w-2xl bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">
              Action Details
            </DialogTitle>
          </DialogHeader>
          {viewLogDetails && (
            <div className="py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="font-bold">Timestamp</Label>
                  <p className="text-sm">{new Date(viewLogDetails.timestamp).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="font-bold">Employee</Label>
                  <p className="text-sm">{viewLogDetails.user_name} ({viewLogDetails.user_email})</p>
                </div>
                <div>
                  <Label className="font-bold">Action Type</Label>
                  <p className="text-sm">{viewLogDetails.action_type}</p>
                </div>
                <div>
                  <Label className="font-bold">Module</Label>
                  <p className="text-sm">{viewLogDetails.module_name}</p>
                </div>
                <div>
                  <Label className="font-bold">Entity Type</Label>
                  <p className="text-sm">{viewLogDetails.entity_type}</p>
                </div>
                <div>
                  <Label className="font-bold">Entity Code</Label>
                  <p className="text-sm">{viewLogDetails.entity_code || 'N/A'}</p>
                </div>
              </div>
              
              <div>
                <Label className="font-bold">Description</Label>
                <p className="text-sm">{viewLogDetails.description}</p>
              </div>
              
              {viewLogDetails.old_data && (
                <div>
                  <Label className="font-bold">Previous Data</Label>
                  <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto">
                    {JSON.stringify(viewLogDetails.old_data, null, 2)}
                  </pre>
                </div>
              )}
              
              {viewLogDetails.new_data && (
                <div>
                  <Label className="font-bold">New Data</Label>
                  <pre className="text-xs bg-gray-100 p-3 rounded overflow-x-auto">
                    {JSON.stringify(viewLogDetails.new_data, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserDialogOpen} onOpenChange={setCreateUserDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">
              Create New User
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="username" className="font-bold">Username *</Label>
              <Input
                id="username"
                value={newUserData.username}
                onChange={(e) => setNewUserData({ ...newUserData, username: e.target.value })}
                placeholder="Enter username"
                className="border-2 border-black"
              />
            </div>
            <div>
              <Label htmlFor="password" className="font-bold">Password *</Label>
              <Input
                id="password"
                type="password"
                value={newUserData.password}
                onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                placeholder="Enter password"
                className="border-2 border-black"
              />
            </div>
            <div>
              <Label htmlFor="name" className="font-bold">Full Name *</Label>
              <Input
                id="name"
                value={newUserData.name}
                onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                placeholder="Enter full name"
                className="border-2 border-black"
              />
            </div>
            <div>
              <Label htmlFor="title" className="font-bold">Title</Label>
              <Input
                id="title"
                value={newUserData.title}
                onChange={(e) => setNewUserData({ ...newUserData, title: e.target.value })}
                placeholder="e.g., Manager, Designer"
                className="border-2 border-black"
              />
            </div>
            <div>
              <Label htmlFor="department" className="font-bold">Department</Label>
              <Input
                id="department"
                value={newUserData.department}
                onChange={(e) => setNewUserData({ ...newUserData, department: e.target.value })}
                placeholder="e.g., Design, Production"
                className="border-2 border-black"
              />
            </div>
            <div>
              <Label htmlFor="access_level" className="font-bold">Access Level</Label>
              <Input
                id="access_level"
                value={newUserData.access_level}
                onChange={(e) => setNewUserData({ ...newUserData, access_level: e.target.value })}
                placeholder="e.g., Employee, Manager"
                className="border-2 border-black"
              />
            </div>
            
            {/* GPS Restriction */}
            <div className="border-2 border-orange-400 bg-orange-50 p-4 rounded">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newUserData.gps_restricted}
                  onChange={(e) => setNewUserData({ ...newUserData, gps_restricted: e.target.checked })}
                  className="w-5 h-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-orange-500"
                />
                <div className="flex-1">
                  <span className="text-sm font-bold text-orange-800">Centralized GPS Access Control</span>
                  <p className="text-xs text-gray-700 mt-1">
                    Restrict this user to access the application only from allowed GPS locations
                  </p>
                </div>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setCreateUserDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateUser}
              className="bg-black hover:bg-gray-800"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialogs */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteUser}
        title="Delete User"
        description={`Are you sure you want to delete ${userToDelete?.name}? This action cannot be undone.`}
      />

      <AuthorizationDialog
        open={deleteAuthDialogOpen}
        onOpenChange={setDeleteAuthDialogOpen}
        onAuthorized={confirmDeleteUser}
        correctCode="1436"
        title="Delete User Authorization"
        description="Enter authorization code to confirm user deletion"
      />

      {/* Delete Audit Log Confirmation Dialogs */}
      <ConfirmDialog
        open={deleteLogDialogOpen}
        onOpenChange={setDeleteLogDialogOpen}
        onConfirm={handleDeleteLog}
        title="Delete Audit Log"
        description={`Are you sure you want to delete this audit log entry? This action cannot be undone.`}
      />

      <AuthorizationDialog
        open={deleteLogAuthDialogOpen}
        onOpenChange={setDeleteLogAuthDialogOpen}
        onAuthorized={confirmDeleteLog}
        correctCode="1436"
        title="Delete Audit Log Authorization"
        description="Enter authorization code to confirm audit log deletion"
      />

      {/* Permissions Dialog */}
      <Dialog open={permissionsDialogOpen} onOpenChange={setPermissionsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
          <DialogHeader className="border-b-2 border-black pb-4">
            <DialogTitle className="text-xl font-bold tracking-tight uppercase">
              Manage Permissions - {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          
          {permissions && (
            <div className="py-4 space-y-6">
              {/* Quick Actions */}
              <div className="flex gap-3 pb-4 border-b-2 border-gray-200">
                <Button
                  onClick={() => handleGrantAll(selectedUser.id)}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  <Check className="w-4 h-4 mr-2" />
                  Grant All Permissions
                </Button>
                <Button
                  onClick={() => handleRevokeAll(selectedUser.id)}
                  variant="outline"
                  className="border-red-500 text-red-600 hover:bg-red-50"
                  disabled={loading}
                >
                  <X className="w-4 h-4 mr-2" />
                  Revoke All Permissions
                </Button>
              </div>

              {/* Module Access - Top Priority */}
              <div className="border-2 border-gray-200 p-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">
                  Module Access
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={permissions.design_studio_access || false}
                      onChange={() => handlePermissionToggle('design_studio_access')}
                      className="w-5 h-5 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                    />
                    <span className="text-sm font-medium">Design Studio Access</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={permissions.task_management_access || false}
                      onChange={() => handlePermissionToggle('task_management_access')}
                      className="w-5 h-5 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                    />
                    <span className="text-sm font-medium">Task Management Access</span>
                  </label>
                  <label className="flex items-center gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50">
                    <input
                      type="checkbox"
                      checked={permissions.user_access_control || false}
                      onChange={() => handlePermissionToggle('user_access_control')}
                      className="w-5 h-5 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                    />
                    <span className="text-sm font-medium">User Access Control</span>
                  </label>
                </div>
              </div>

              {/* Permission Groups */}
              {Object.entries(permissionGroups).map(([groupName, perms]) => (
                <div key={groupName} className="border-2 border-gray-200 p-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-gray-700 mb-3">
                    {groupName}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {perms.map(({ key, label }) => (
                      <label
                        key={key}
                        className="flex items-center gap-3 p-3 border border-gray-200 rounded cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={permissions[key] || false}
                          onChange={() => handlePermissionToggle(key)}
                          className="w-5 h-5 text-black border-2 border-gray-300 rounded focus:ring-2 focus:ring-black"
                        />
                        <span className="text-sm font-medium">{label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}

              {/* GPS Restriction - Separate Section */}
              <div className="border-2 border-orange-400 bg-orange-50 p-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-orange-800 mb-3">
                  GPS Restriction (Centralized Access)
                </h3>
                <label className="flex items-center gap-3 p-3 border border-orange-200 bg-white rounded cursor-pointer hover:bg-orange-50">
                  <input
                    type="checkbox"
                    checked={permissions.gps_restricted || false}
                    onChange={() => handlePermissionToggle('gps_restricted')}
                    className="w-5 h-5 text-orange-600 border-2 border-gray-300 rounded focus:ring-2 focus:ring-orange-500"
                  />
                  <span className="text-sm font-medium">Restrict user to allowed GPS locations only</span>
                </label>
              </div>

              {/* Save Button */}
              <div className="flex justify-end gap-3 pt-4 border-t-2 border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setPermissionsDialogOpen(false)}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSavePermissions}
                  className="bg-black hover:bg-gray-800"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Permissions'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
}
